import type { FunctionComponent } from 'react';
import React, { useCallback, useEffect } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { ErrorBoundary, sendErrorEvent } from '@trello/error-reporting';
import { forTemplate } from '@trello/i18n';
import { Button } from '@trello/nachos/button';

import { navigate } from 'app/scripts/controller/navigate';
import { useInlineInspirationsPopoverQuery } from './InlineInspirationsPopoverQuery.generated';

import styles from './InlineInspirationsPopover.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import wavingHandSvg from 'resources/images/inline-inspirations/waving_hand.svg';

const format = forTemplate('inline_inspirations');
const DISMISS_MESSAGE = 'inline-inspirations-popover';

interface InlineInspirationsPopoverProps {
  boardId: string;
  orgId: string;
  onClose: () => void;
  returnBoardShortLink: string;
}

export const InlineInspirationsPopover: FunctionComponent<InlineInspirationsPopoverProps> = ({
  boardId,
  orgId,
  onClose,
  returnBoardShortLink,
}) => {
  const { data } = useInlineInspirationsPopoverQuery({
    variables: {
      boardId: boardId || '',
    },
    waitOn: ['CurrentBoardFull'],
  });

  const {
    isOneTimeMessageDismissed,
    dismissOneTimeMessage,
  } = useOneTimeMessagesDismissed();

  const boardCreatorId = data?.board?.idMemberCreator;
  const boardMemberCount = data?.board?.members.length;
  const boardMemberType = data?.board?.memberships.map(
    (member) => member.memberType,
  );

  useEffect(() => {
    if (!isOneTimeMessageDismissed(DISMISS_MESSAGE)) {
      dismissOneTimeMessage(DISMISS_MESSAGE);
    }

    Analytics.sendScreenEvent({
      name: 'inlineInspirationBoardPromptInlineDialog',
      attributes: {
        boardId,
        boardCreatorId,
        boardMemberCount,
        orgId,
      },
    });
  }, [
    boardCreatorId,
    boardId,
    boardMemberCount,
    boardMemberType,
    dismissOneTimeMessage,
    isOneTimeMessageDismissed,
    orgId,
  ]);

  const onDismiss = useCallback(async () => {
    try {
      Analytics.sendClickedButtonEvent({
        buttonName: 'useThisBoardButton',
        source: 'inlineInspirationBoardPromptDialog',
        attributes: {
          boardId,
          boardCreatorId,
          boardMemberCount,
          orgId,
        },
      });
    } catch (err) {
      sendErrorEvent(err, {
        tags: {
          ownershipArea: 'trello-mocha',
          feature: 'Inline Inspirations',
        },
        extraData: {
          component: 'InlineInspirationsModalComponent',
        },
      });
    }
    onClose();
  }, [boardCreatorId, boardId, boardMemberCount, onClose, orgId]);

  const onStartFreshClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'boardPromptCreateNewButton',
      source: 'inlineInspirationBoardPromptDialog',
      attributes: {
        boardId,
        boardCreatorId,
        boardMemberCount,
        orgId,
      },
    });

    navigate(`/b/${returnBoardShortLink}`, { trigger: true });
  }, [boardCreatorId, boardId, boardMemberCount, returnBoardShortLink, orgId]);

  return (
    <ErrorBoundary
      tags={{ ownershipArea: 'trello-mocha', feature: 'Inline Inspirations' }}
    >
      {
        <div className={styles.inlineInspirationsPopoverWrapper}>
          <img
            className={styles.inlineInspirationsPopoverImg}
            alt=""
            src={wavingHandSvg}
          />
          <h2 className={styles.inlineInspirationsTitle}>
            {format('inspiration-modal-header')}
          </h2>
          <div className={styles.inlineInspirationDescription}>
            {format('inspiration-modal-paragraph')}
          </div>
          <div className={styles.inlineInspirationButtonsContainer}>
            <Button
              className={styles.inlineInspirationsYesToBoard}
              appearance="primary"
              onClick={onDismiss}
            >
              {format('yes-to-suggested-board')}
            </Button>
            <Button
              className={styles.inlineInspirationsStartFresh}
              appearance="link"
              onClick={onStartFreshClick}
            >
              {format('start-fresh')}
            </Button>
          </div>
        </div>
      }
    </ErrorBoundary>
  );
};
